import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { randomId } from 'server/shared/helpers/random';
import { AssignmentTypeTabEnum } from 'server/shared/models/assignmentTypeTab.enum';
import { Certificate } from 'server/shared/models/certificate';
import { Customer } from 'server/shared/models/customer';
import { RoleEnum } from 'server/shared/models/role.enum';
import {
  ConditionalTransferConditionGroup,
  ConditionalTransferOptions,
  TransferTargetCustomerGroup,
} from 'server/shared/models/transfer';
import { DispoUser } from 'server/shared/models/user';
import { ToastService } from '../../services/toast/toast.service';

type ConditionalTransferOptionsWithPossibleCustomers = Omit<
  ConditionalTransferOptions,
  'conditions'
> & {
  conditions: ConditionalTransferConditionGroupWithPossibleCustomers[];
};

interface ConditionalTransferConditionGroupWithPossibleCustomers
  extends ConditionalTransferConditionGroup {
  possibleCustomerIds: string[];
}

type TransferCustomer = {
  id: string;
  displayName: string;
};

@Component({
  selector: 'admin-conditional-transfers',
  templateUrl: 'conditional-transfers.html',
  styleUrls: ['conditional-transfers.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConditionalTransfersComponent implements OnInit, OnDestroy {
  private _tab: string = '';
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  user$ = new BehaviorSubject<DispoUser | undefined>(undefined);
  jobConditionalTransfers$ = new BehaviorSubject<
    ConditionalTransferOptionsWithPossibleCustomers[]
  >([]);
  employmentConditionalTransfers$ = new BehaviorSubject<
    ConditionalTransferOptionsWithPossibleCustomers[]
  >([]);
  conditionalTransfers$ = new BehaviorSubject<
    ConditionalTransferOptionsWithPossibleCustomers[]
  >([]);

  possibleJobCustomerIds$ = new BehaviorSubject<string[]>([]);
  possibleEmploymentCustomerIds$ = new BehaviorSubject<string[]>([]);

  tabs: { label: string; value: AssignmentTypeTabEnum }[] = [
    {
      label: 'jobsPool',
      value: AssignmentTypeTabEnum.JOBS,
    },
    {
      label: 'employmentsPool',
      value: AssignmentTypeTabEnum.EMPLOYMENTS,
    },
  ];

  tabParam$: Observable<AssignmentTypeTabEnum> = this.route.queryParams.pipe(
    map((params) => {
      this._tab = params.tab ?? AssignmentTypeTabEnum.JOBS;
      this.setConditionalTransfers();
      return params.tab ?? AssignmentTypeTabEnum.JOBS;
    }),
    // distinctUntilChanged is needed in case of other params changing
    distinctUntilChanged(),
  );
  currentUserTracker?: Tracker.Computation;

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.currentUserTracker = Tracker.autorun(() => {
      const user = Users.findOne({
        _id: Meteor.userId(),
        role: RoleEnum.DISPO,
      }) as DispoUser;

      let customer = Customers.findOne({ _id: user?.customerId });
      this.ngZone.run(() => {
        if (
          user &&
          !user.dispoPermissions?.dispoAccess?.featurePermissions
            ?.isCustomerAdmin
        ) {
          this.historyBack();
        }
        this.customer$.next(customer);
        this.user$.next(user);
        this.setPossibleCustomers();
        this.setConditionalTransfers();
      });
    });
  }
  ngOnDestroy() {
    this.currentUserTracker?.stop();
  }

  switchTab(tab: string): void {
    this._tab = tab;

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tab },
        replaceUrl: true,
        queryParamsHandling: 'merge',
      })
      .then();
  }

  setConditionalTransfers(reset?: boolean): void {
    if (!this._tab) return;
    const customer = this.customer$.value;
    const possibleJobCustomerIds = this.possibleJobCustomerIds$.value;
    const possibleEmploymentCustomerIds =
      this.possibleEmploymentCustomerIds$.value;

    if (!this.jobConditionalTransfers$.value.length || reset) {
      const newJobConditionalTransfers: ConditionalTransferOptionsWithPossibleCustomers[] =
        [];
      customer?.customerTransfers?.transferConditions?.jobTransferConditions?.forEach(
        (conditionalTransfer) => {
          let conditionWithPossibleCustomers =
            conditionalTransfer.conditions.map((condition) => {
              return {
                ...condition,
                possibleCustomerIds: [...possibleJobCustomerIds],
              };
            });

          newJobConditionalTransfers.push({
            ...conditionalTransfer,
            conditions: conditionWithPossibleCustomers,
          });
        },
      );
      newJobConditionalTransfers.forEach((conditionalTransfer) => {
        conditionalTransfer.conditions.forEach((transferConditionGroup) => {
          let existingCustomerIds: string[] = [];
          transferConditionGroup.transferCustomerGroups.forEach((group) => {
            group.preferredTransferCustomers.forEach((customer) => {
              existingCustomerIds.push(customer.id);
            });
          });
          transferConditionGroup.possibleCustomerIds =
            possibleJobCustomerIds.filter((id) => {
              return !existingCustomerIds.includes(id);
            });
        });
      });
      this.jobConditionalTransfers$.next(newJobConditionalTransfers);
    }
    if (!this.employmentConditionalTransfers$.value.length || reset) {
      const newEmploymentConditionalTransfers: ConditionalTransferOptionsWithPossibleCustomers[] =
        [];
      customer?.customerTransfers?.transferConditions?.employmentTransferConditions?.forEach(
        (conditionalTransfer) => {
          let conditionWithPossibleCustomers =
            conditionalTransfer.conditions.map((condition) => {
              return {
                ...condition,
                possibleCustomerIds: [...possibleEmploymentCustomerIds],
              };
            });

          newEmploymentConditionalTransfers.push({
            ...conditionalTransfer,
            conditions: conditionWithPossibleCustomers,
          });
        },
      );
      newEmploymentConditionalTransfers.forEach((conditionalTransfer) => {
        let existingCustomerIds: string[] = [];
        conditionalTransfer.conditions.forEach((transferConditionGroup) => {
          transferConditionGroup.possibleCustomerIds =
            possibleEmploymentCustomerIds.filter((id) => {
              return !existingCustomerIds.includes(id);
            });
          transferConditionGroup.transferCustomerGroups.forEach((group) => {
            group.preferredTransferCustomers.forEach((customer) => {
              existingCustomerIds.push(customer.id);
            });
          });
        });
      });
      this.employmentConditionalTransfers$.next(
        newEmploymentConditionalTransfers,
      );
    }
    if (this._tab === AssignmentTypeTabEnum.JOBS) {
      this.conditionalTransfers$.next(this.jobConditionalTransfers$.value);
    } else {
      this.conditionalTransfers$.next(
        this.employmentConditionalTransfers$.value,
      );
    }
  }

  setPossibleCustomers() {
    const customer = this.customer$.value;
    const possibleJobCustomerIds =
      customer?.customerTransfers?.transfers?.map(
        (customer) => customer.targetCustomer.id,
      ) || [];
    const possibleEmploymentCustomerIds =
      customer?.customerTransfers?.employmentTransfers?.map(
        (customer) => customer.targetCustomer.id,
      ) || [];

    this.possibleJobCustomerIds$.next(possibleJobCustomerIds);
    this.possibleEmploymentCustomerIds$.next(possibleEmploymentCustomerIds);
  }

  addNewConditionalTransfer(): void {
    const possibleJobCustomerIds = this.possibleJobCustomerIds$.value;
    const possibleEmploymentCustomerIds =
      this.possibleEmploymentCustomerIds$.value;

    let newConditionalTransfer: ConditionalTransferOptionsWithPossibleCustomers =
      {
        id: randomId(),
        certificates: [],
        conditions: [
          {
            hoursBeforeJobStart: 0,
            transferCustomerGroups: [],
            possibleCustomerIds:
              this._tab === AssignmentTypeTabEnum.JOBS
                ? [...possibleJobCustomerIds]
                : [...possibleEmploymentCustomerIds],
          },
        ],
      };
    if (this._tab === AssignmentTypeTabEnum.JOBS) {
      const currentTransfers = this.jobConditionalTransfers$.value;
      currentTransfers.push(newConditionalTransfer);
      this.jobConditionalTransfers$.next([...currentTransfers]);
    } else {
      const currentTransfers = this.employmentConditionalTransfers$.value;
      currentTransfers.push(newConditionalTransfer);
      this.employmentConditionalTransfers$.next([...currentTransfers]);
    }
  }

  removeConditionalTransfer(id: string): void {
    if (this._tab === AssignmentTypeTabEnum.JOBS) {
      const filteredTransfers = this.jobConditionalTransfers$.value.filter(
        (condition) => condition.id !== id,
      );
      this.jobConditionalTransfers$.next(filteredTransfers);
    } else {
      const filteredTransfers =
        this.employmentConditionalTransfers$.value.filter(
          (condition) => condition.id !== id,
        );
      this.employmentConditionalTransfers$.next(filteredTransfers);
    }
    const filteredConditional = this.conditionalTransfers$.value.filter(
      (condition) => condition.id !== id,
    );
    this.conditionalTransfers$.next(filteredConditional);
  }

  cancelChanges() {
    this.setConditionalTransfers(true);
  }

  saveConditionalTransfers(): void {
    const customer = this.customer$.value;
    if (!customer) return;

    let jobConditionalTransfers: ConditionalTransferOptions[] =
      this.jobConditionalTransfers$.value.map((conditionalTransfer) => {
        let conditions = conditionalTransfer.conditions.map((condition) => {
          const { possibleCustomerIds, ...conditionalTransferProps } =
            condition;
          return conditionalTransferProps;
        });
        return { ...conditionalTransfer, conditions };
      });

    let employmentConditionalTransfers: ConditionalTransferOptions[] =
      this.employmentConditionalTransfers$.value.map((conditionalTransfer) => {
        let conditions = conditionalTransfer.conditions.map((condition) => {
          const { possibleCustomerIds, ...conditionalTransferProps } =
            condition;
          return conditionalTransferProps;
        });
        return { ...conditionalTransfer, conditions };
      });

    Meteor.call(
      'updateConditionalTransfers',
      customer._id,
      jobConditionalTransfers,
      employmentConditionalTransfers,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  addNewCustomerGroup(transferCondition: ConditionalTransferConditionGroup) {
    transferCondition.transferCustomerGroups.push({
      preferredTransferCustomers: [],
    });
  }

  certificatesSelected(
    transferCondition: ConditionalTransferOptions,
    certificates: Certificate[],
  ) {
    transferCondition.certificates = certificates.map((certificate) => {
      return {
        id: certificate._id,
        displayName: certificate.name,
      };
    });
  }

  updateCustomersOfGroup(
    selection: TransferCustomer[],
    transferCustomerGroup: TransferTargetCustomerGroup,
    conditionalTransfer: ConditionalTransferConditionGroupWithPossibleCustomers,
  ) {
    let removedCustomers =
      transferCustomerGroup.preferredTransferCustomers.filter((customer) => {
        return !selection.map((customer) => customer.id).includes(customer.id);
      });
    transferCustomerGroup.preferredTransferCustomers = selection;
    conditionalTransfer.possibleCustomerIds =
      conditionalTransfer.possibleCustomerIds.concat(
        removedCustomers.map((customer) => customer.id),
      );
    conditionalTransfer.possibleCustomerIds =
      conditionalTransfer.possibleCustomerIds.filter((id) => {
        return !selection.map((customer) => customer.id).includes(id);
      });
  }

  historyBack() {
    this.location.back();
  }

  increaseConditionPriority(
    transferCondition: ConditionalTransferOptionsWithPossibleCustomers,
    index: number,
  ) {
    let temp = transferCondition.conditions[index];
    transferCondition.conditions.splice(index, 1);
    transferCondition.conditions.splice(index - 1, 0, temp);
  }

  decreaseConditionPriority(
    transferCondition: ConditionalTransferOptionsWithPossibleCustomers,
    index: number,
  ) {
    let temp = transferCondition.conditions[index];
    transferCondition.conditions.splice(index, 1);
    transferCondition.conditions.splice(index + 1, 0, temp);
  }

  removeCustomerGroup(
    conditionalTransfer: ConditionalTransferConditionGroup,
    index: number,
  ) {
    conditionalTransfer.transferCustomerGroups.splice(index, 1);
  }

  addNewConditionGroup(
    transferCondition: ConditionalTransferOptionsWithPossibleCustomers,
  ) {
    const possibleJobCustomerIds = this.possibleJobCustomerIds$.value;
    const possibleEmploymentCustomerIds =
      this.possibleEmploymentCustomerIds$.value;

    transferCondition.conditions.push({
      hoursBeforeJobStart: 0,
      transferCustomerGroups: [],
      possibleCustomerIds:
        this._tab === AssignmentTypeTabEnum.JOBS
          ? [...possibleJobCustomerIds]
          : [...possibleEmploymentCustomerIds],
    });
  }
  removeConditionGroup(
    conditionalTransfer: ConditionalTransferOptionsWithPossibleCustomers,
    index: number,
  ) {
    conditionalTransfer.conditions.splice(index, 1);
  }

  increaseCustomerGroupPriority(
    conditionalTransfer: ConditionalTransferConditionGroup,
    index: number,
  ) {
    let temp = conditionalTransfer.transferCustomerGroups[index];
    conditionalTransfer.transferCustomerGroups.splice(index, 1);
    conditionalTransfer.transferCustomerGroups.splice(index - 1, 0, temp);
  }

  decreaseCustomerGroupPriority(
    conditionalTransfer: ConditionalTransferConditionGroup,
    index: number,
  ) {
    let temp = conditionalTransfer.transferCustomerGroups[index];
    conditionalTransfer.transferCustomerGroups.splice(index, 1);
    conditionalTransfer.transferCustomerGroups.splice(index + 1, 0, temp);
  }
}
